import {ref, computed, Ref} from 'vue'
import {Graphql, CrudFormSchemaBuilder, CrudCollection} from '@hc/graphql'
import _ from 'lodash'
import {CrudObject} from "@hc/graphql";

export interface UseCrudFormParams {
  collection: CrudCollection | String,
  operation?: string,
  state?: string
  id?: number,
  object?: CrudObject,
  parentId?: number,
  parentKey?: string,
  parentEditable?: boolean,
  disableGrid?: boolean,
  fields?: Array<string>
}



export function useCrudForm(params: UseCrudFormParams){

  const formCollection: CrudCollection = typeof params.collection == 'object' ? params.collection as CrudCollection: Graphql.collection(params.collection)
  const formLoading = ref(false)
  const formObject: Ref<CrudObject> = ref(null)
  const formError = ref(null)
  const formSchema = ref(Graphql.formSchema(formCollection.name, params.operation, params.state))
  const formParentEditable = params.parentEditable != undefined ? params.parentEditable : formCollection.parent && !params.id && !params.parentId

  const formGeneratorSchema = ref(new CrudFormSchemaBuilder({
    collection: formCollection,
    formSchema: formSchema.value,
    parentKey: params.parentKey,
    parentId: params.parentId,
    fields: params.fields,
    disableGrid: params.disableGrid,
  }).build())

  const formGeneratorOptions = {
    validateAfterLoad: false,
    validateAfterChanged: false,
    validateAsync: true
  }

  const formDefaultObject = (): any => {
    const data = {
      __typename: formCollection.name,
    }
    const formFields = formGeneratorSchema.value.fields.map((f) => f.model)
    const properties = [].concat(formFields).concat(formCollection.joinNames)
    for(const property of properties){
      if(data[property]) {
        continue
      }
      const formField = formGeneratorSchema.value.fields.find((f) => f.model == property)
      if(formField && !_.isUndefined(formField.default)){
        data[property] = formField.default
      } else {
        data[property] = null
      }

    }
    if(params.parentKey && params.parentId){
      data[params.parentKey] = params.parentId
      const parentJoinName = formCollection.joinNameForKey(params.parentKey)
      if(parentJoinName){
        formCollection.join(parentJoinName).find(params.parentId).then((parent)=> {
          data[parentJoinName] = parent
        })
      }
    }
    return data
  }

  formObject.value = params.object || formDefaultObject()

  const formData = computed(() => {
    const data = {}
    for(const field of formCollection.formFields) {
      const formGeneratorField = formGeneratorSchema.value.fields.find((f) => f.model == field)
      if(formGeneratorField && formGeneratorField.disabled) continue
      data[field] = formObject.value[field]
    }
    return data
  })

  const formLoadSchema = async () => {
    formSchema.value = Graphql.formSchema(formCollection.name, params.operation, formObject.value.status)
    let formParentEditableReal = formParentEditable
    if(formCollection.parent && !formParentEditableReal){
      if(!formObject.value[formCollection.parentKey]){
        formParentEditableReal = true
      }
    }
    formGeneratorSchema.value = new CrudFormSchemaBuilder({
      collection: formCollection,
      formSchema: formSchema.value,
      parentKey: params.parentKey,
      parentId: params.parentId,
      fields: params.fields,
      disableGrid: params.disableGrid
    }).build()
  }

  const formLoadObject = async (id: number = null) => {
    formLoading.value = true
    const result = await formCollection.find(id)
    formObject.value = result
    await formLoadSchema()
    formLoading.value = false
  }

  const formSaveDraft = () => {
    const key = `wonderland.form.${formCollection.name}[${formObject.value.id || ''}]`
    localStorage.setItem(key, JSON.stringify(formData.value))
  }

  const formLoadDraft = () => {
    const key = `wonderland.form.${formCollection.name}[${formObject.value.id || ''}]`
    if(!localStorage.getItem(key)) return
    const draft = JSON.parse(localStorage.getItem(key))
    if(params.parentId){
      draft[formCollection.parentKey] = params.parentId
    }
    Object.assign(formObject.value, draft)
  }

  const formSave = async () => {
    formLoading.value = true
    formError.value = null
    try {
      if(formObject.value.id) {
        formObject.value = await formCollection.update(formObject.value.id, formData.value)
      } else {
        formObject.value = await formCollection.create(formData.value)
      }
      return true
    } catch(e) {
      formError.value = e
      return false
    } finally {
      formLoading.value = false
    }
  }

  if(params.object) {
    formLoadSchema()
  }
  else if(params.id){
    formLoadObject(params.id)
  }

  //formLoadDraft()

  return {
    formCollection,
    formSchema,
    formGeneratorSchema,
    formLoading,
    formError,
    formGeneratorOptions,
    formObject,
    formData,
    formSaveDraft,
    formLoadDraft,
    formLoadObject,
    formLoadSchema,
    formSave,
  }
}

